import React, { useState } from 'react';
import {
  Box,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Button,
  Text,
  Heading,
  RefreshIcon,
  TableToolbar,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { useMissingInformationReport } from './hooks/useMissingInformationReport';
import { getTableToolbarText } from './utils';
import { getStatusBadge } from './viewHelpers';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { MissingBankAccount } from 'api/types/missingInformationReportTypes';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ExportReportButton } from './components/ExportReportButton';
import { BankAccountDetailsDrawer } from './components/BankAccountDetailsDrawer';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { InfoIcon } from '@razorpay/blade/components';

const VERIFICATION_TOOLTIP =
  'We verify accounts via a Penny Test. This confirms the account is active and retrieves the registered name from the bank';

export const UnverifiedFailedBankAccount = () => {
  const [selectedAccount, setSelectedAccount] = useState<MissingBankAccount | null>(null);
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const {
    data: employeeData,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useMissingInformationReport(
    'unverified_failed_bank_account_details',
    currentPage,
    itemsPerPage,
  );

  const shouldShowEmptyView =
    !isError && !isLoading && !isFetching && employeeData?.data.items.length === 0;
  const shouldShowErrorView = isError;
  const shouldShowTable = !shouldShowEmptyView && !shouldShowErrorView;

  return (
    <Box>
      <Heading
        size="large"
        weight="semibold"
        color="surface.text.gray.normal"
        marginBottom="spacing.2">
        Unverified & Failed Bank Accounts
      </Heading>
      <Text color="surface.text.gray.subtle" marginBottom="spacing.7">
        Employees whose bank accounts are pending or have failed penny-drop verification
      </Text>

      {isError && <ErrorView onRefetch={refetch} />}
      {shouldShowEmptyView && (
        <EmptyListView
          title="🔍 No Missing Information Found"
          description="Great! All employees have completed their details."
        />
      )}
      {shouldShowTable && (
        <Table
          isLoading={isLoading}
          isRefreshing={isFetching}
          data={{
            nodes: employeeData?.data.items || [],
          }}
          backgroundColor="surface.background.gray.intense"
          toolbar={
            <TableToolbar
              title={getTableToolbarText(
                employeeData?.data.total_count || 0,
                currentPage,
                itemsPerPage,
              )}>
              <Box display="flex" justifyContent="flex-end" gap="spacing.5">
                <ExportReportButton type="unverified_failed_bank_account_details" />
                <Button
                  icon={RefreshIcon}
                  variant="tertiary"
                  accessibilityLabel="Refresh Unverified & Failed Bank Accounts"
                  onClick={refetch}
                  isDisabled={isFetching || isLoading}
                />
              </Box>
            </TableToolbar>
          }
          pagination={
            <TablePagination
              paginationType="server"
              showPageSizePicker={true}
              currentPage={currentPage}
              totalItemCount={employeeData?.data.total_count || 0}
              onPageChange={({ page }) => setCurrentPage(page)}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
            />
          }>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Account Number</TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" alignItems="center" gap="spacing.2">
                      <Text>Status</Text>
                      <BladeTooltipWrapper content={VERIFICATION_TOOLTIP} placement="top">
                        <InfoIcon size="small" color="surface.icon.gray.muted" />
                      </BladeTooltipWrapper>
                    </Box>
                  </TableHeaderCell>
                  <TableHeaderCell>IFSC Code</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((employee) => (
                  <TableRow
                    key={employee.id}
                    item={employee}
                    hoverActions={
                      <Box display="flex" gap="spacing.4">
                        <Button
                          size="xsmall"
                          variant="secondary"
                          onClick={() => setSelectedAccount(employee)}>
                          View Details
                        </Button>
                      </Box>
                    }>
                    <TableCell>
                      <Box>
                        <Text color="surface.text.gray.subtle">{employee.name}</Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {employee.employee_id}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text color="surface.text.gray.subtle">{employee.account_number || '-'}</Text>
                    </TableCell>
                    <TableCell>{getStatusBadge(employee.verification_status)}</TableCell>
                    <TableCell>
                      <Text color="surface.text.gray.subtle">{employee.ifsc || '-'}</Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      )}
      <BankAccountDetailsDrawer
        selectedAccount={selectedAccount}
        onClose={() => setSelectedAccount(null)}
        variant="unverified_failed"
      />
    </Box>
  );
};
